import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import type { MutableRef } from 'preact/hooks'
import { trackingApi } from '@/api/tracking'
import { clientCompanyApi } from '@/api/clientCompany'
import { useCompany } from '@/hooks/useCompany'
import { useToast } from '@/hooks/useToast'
import type { ApiResponse } from '@/api/client'
import type { CompanyTrackingViewDto } from '@/types/tracking'

/** The three custom-script fields, edited together and saved in one PATCH. */
export interface CustomScriptFields {
  CustomScriptHeader: string
  CustomScriptBody: string
  CustomScriptFooter: string
}

export interface UseCompanyTrackingResult {
  tracking: CompanyTrackingViewDto | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  setEnabled: (next: boolean) => Promise<void>
  /** PATCHes the three custom-script fields. Returns the raw response so callers can
   *  surface per-field 422 validation errors inline (global toasts still fire). */
  saveScripts: (scripts: CustomScriptFields) => Promise<ApiResponse<CompanyTrackingViewDto> | null>
  /** PATCHes the custom approved-domains allowlist. Returns the raw response so callers can
   *  surface 422 validation errors inline (global toasts still fire). */
  saveApprovedDomains: (domains: string[]) => Promise<ApiResponse<CompanyTrackingViewDto> | null>
}

interface TrackingFetcher {
  tracking: CompanyTrackingViewDto | null
  setTracking: (t: CompanyTrackingViewDto | null) => void
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  etagRef: MutableRef<string | null>
}

/** Loads the tracking config and tracks its ETag header. */
function useTrackingFetcher(clientCompanyId: string): TrackingFetcher {
  const { showErrorUnless422 } = useToast()
  const [tracking, setTracking] = useState<CompanyTrackingViewDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const etagRef = useRef<string | null>(null)
  const hasLoadedOnce = useRef(false)

  const refetch = useCallback(async () => {
    if (!clientCompanyId) return
    if (!hasLoadedOnce.current) setIsLoading(true)
    setError(null)
    const res = await trackingApi.getCompanyTracking(clientCompanyId)
    if (!res.data) {
      const message = res.error || 'Failed to load tracking config'
      setError(message)
      showErrorUnless422('Load failed', res, message)
      if (!hasLoadedOnce.current) {
        hasLoadedOnce.current = true
        setIsLoading(false)
      }
      return
    }
    setTracking(res.data)
    etagRef.current = res.etag ?? null
    if (!hasLoadedOnce.current) {
      hasLoadedOnce.current = true
      setIsLoading(false)
    }
  }, [clientCompanyId, showErrorUnless422])

  useEffect(() => {
    hasLoadedOnce.current = false
    setTracking(null)
    etagRef.current = null
    void refetch()
  }, [refetch])

  return useMemo(
    () => ({ tracking, setTracking, isLoading, error, refetch, etagRef }),
    [tracking, isLoading, error, refetch],
  )
}

/** Returns a setEnabled callback that PATCHes the toggle and refreshes the
 *  parent ClientCompany context (so the next PATCH sees the fresh ETag). */
function useToggleEnabled(clientCompanyId: string, fetcher: TrackingFetcher) {
  const { showErrorUnless422, showSuccess } = useToast()
  const { company, updateCompany } = useCompany()

  return useCallback(async (next: boolean) => {
    if (!clientCompanyId || !fetcher.tracking) return

    const previous = fetcher.tracking
    fetcher.setTracking({ ...previous, Enabled: next })

    // API validates concurrency on the parent ClientCompany; ETag header on
    // the tracking endpoint isn't reliably exposed via CORS, so fall back to
    // the ClientCompany body DTO's ETag.
    const etag = fetcher.etagRef.current ?? company?.ETag ?? ''
    const res = await trackingApi.patchCompanyTracking(clientCompanyId, {
      ETag: etag,
      Enabled: next,
    })

    if (!res.data) {
      fetcher.setTracking(previous)
      showErrorUnless422('Update failed', res, 'Unable to update tracking')
      return
    }

    fetcher.setTracking(res.data)
    fetcher.etagRef.current = res.etag ?? fetcher.etagRef.current
    showSuccess(
      'Tracking updated',
      next ? 'Visitor tracking is now enabled.' : 'Visitor tracking is now disabled.',
    )

    // PATCH bumped ClientCompany.ETag server-side; refresh the context.
    const fresh = await clientCompanyApi.getById(clientCompanyId)
    if (fresh.data) updateCompany(fresh.data)
  }, [clientCompanyId, company?.ETag, fetcher, updateCompany, showErrorUnless422, showSuccess])
}

/** Returns a saveScripts callback that PATCHes the three custom-script fields and,
 *  on success, adopts the returned DTO and refreshes the parent ClientCompany ETag.
 *  422 validation errors are surfaced globally (toasts) and returned to the caller
 *  for inline per-field display; other failures show a toast. */
function useSaveScripts(clientCompanyId: string, fetcher: TrackingFetcher) {
  const { showErrorUnless422, showSuccess } = useToast()
  const { company, updateCompany } = useCompany()

  return useCallback(async (scripts: CustomScriptFields) => {
    if (!clientCompanyId || !fetcher.tracking) return null

    // Concurrency is validated on the parent ClientCompany; fall back to its ETag
    // when the tracking endpoint's ETag header isn't exposed via CORS (see setEnabled).
    const etag = fetcher.etagRef.current ?? company?.ETag ?? ''
    const res = await trackingApi.patchCompanyTracking(clientCompanyId, {
      ETag: etag,
      Enabled: fetcher.tracking.Enabled,
      CustomScriptHeader: scripts.CustomScriptHeader,
      CustomScriptBody: scripts.CustomScriptBody,
      CustomScriptFooter: scripts.CustomScriptFooter,
    })

    if (!res.data) {
      // 422 (invalid markup / too large) is handled globally + inline by the caller;
      // only surface a toast for other failures (concurrency, network, etc.).
      showErrorUnless422('Save failed', res, 'Unable to save custom scripts')
      return res
    }

    fetcher.setTracking(res.data)
    fetcher.etagRef.current = res.etag ?? fetcher.etagRef.current
    showSuccess('Scripts saved', 'Custom tracking scripts updated.')

    // PATCH bumped ClientCompany.ETag server-side; refresh the context.
    const fresh = await clientCompanyApi.getById(clientCompanyId)
    if (fresh.data) updateCompany(fresh.data)
    return res
  }, [clientCompanyId, company?.ETag, fetcher, updateCompany, showErrorUnless422, showSuccess])
}

/** Returns a saveApprovedDomains callback that PATCHes the custom approved-domains allowlist and,
 *  on success, adopts the returned DTO and refreshes the parent ClientCompany ETag. 422 validation
 *  errors are surfaced globally (toasts) and returned to the caller for inline display; other
 *  failures show a toast. Derived domains are read-only and never submitted. */
function useSaveApprovedDomains(clientCompanyId: string, fetcher: TrackingFetcher) {
  const { showErrorUnless422, showSuccess } = useToast()
  const { company, updateCompany } = useCompany()

  return useCallback(async (domains: string[]) => {
    if (!clientCompanyId || !fetcher.tracking) return null

    // Concurrency is validated on the parent ClientCompany; fall back to its ETag
    // when the tracking endpoint's ETag header isn't exposed via CORS (see setEnabled).
    const etag = fetcher.etagRef.current ?? company?.ETag ?? ''
    const res = await trackingApi.patchCompanyTracking(clientCompanyId, {
      ETag: etag,
      Enabled: fetcher.tracking.Enabled,
      ApprovedDomains: domains,
    })

    if (!res.data) {
      // 422 (invalid/too many domains) is handled globally + inline by the caller;
      // only surface a toast for other failures (concurrency, network, etc.).
      showErrorUnless422('Save failed', res, 'Unable to save approved domains')
      return res
    }

    fetcher.setTracking(res.data)
    fetcher.etagRef.current = res.etag ?? fetcher.etagRef.current
    showSuccess('Approved domains saved', 'Approved domains updated.')

    // PATCH bumped ClientCompany.ETag server-side; refresh the context.
    const fresh = await clientCompanyApi.getById(clientCompanyId)
    if (fresh.data) updateCompany(fresh.data)
    return res
  }, [clientCompanyId, company?.ETag, fetcher, updateCompany, showErrorUnless422, showSuccess])
}

export function useCompanyTracking(clientCompanyId: string): UseCompanyTrackingResult {
  const fetcher = useTrackingFetcher(clientCompanyId)
  const setEnabled = useToggleEnabled(clientCompanyId, fetcher)
  const saveScripts = useSaveScripts(clientCompanyId, fetcher)
  const saveApprovedDomains = useSaveApprovedDomains(clientCompanyId, fetcher)
  return {
    tracking: fetcher.tracking,
    isLoading: fetcher.isLoading,
    error: fetcher.error,
    refetch: fetcher.refetch,
    setEnabled,
    saveScripts,
    saveApprovedDomains,
  }
}
